import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid,
  Button,
  Alert,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import '../styles/ArchivedProperties.css';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import NewCustomButton from './common/NewCustomButton';
import { 
  FaArchive, 
  FaTrash, 
  FaUndo,
  FaExclamationTriangle,
  FaArrowLeft 
} from 'react-icons/fa';

import { collection, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { formatDate } from '../utils/dateUtils';
import { useNavigate } from 'react-router-dom';

const ArchivedProperties = () => {
  const { isDarkMode } = useTheme();
  const { currentUser } = useAuth();
  const [archivedProperties, setArchivedProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchArchivedProperties();
  }, [currentUser]);

  const fetchArchivedProperties = async () => {
    try {
      setLoading(true);
      const propertiesRef = collection(db, 'properties');
      const q = query(propertiesRef, where('archived', '==', true));
      const querySnapshot = await getDocs(q);
      
      const properties = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setArchivedProperties(properties);
    } catch (err) {
      console.error('Error fetching archived properties:', err);
      setError('Failed to load archived properties');
    } finally {
      setLoading(false);
    }
  };

  const handleUnarchive = async (propertyId) => {
    try {
      const propertyRef = doc(db, 'properties', propertyId);
      await updateDoc(propertyRef, {
        archived: false,
        archivedDate: null
      });
      
      // Refresh the list
      await fetchArchivedProperties();
    } catch (err) {
      console.error('Error unarchiving property:', err);
      setError('Failed to unarchive property');
    }
  };

  const goBack = () => {
    navigate('/properties');
  };

  if (loading) {
    return <Box sx={{ p: 3 }}>Loading...</Box>;
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <div className="archived-properties-container" role="main" aria-label="Archived Properties">
      <div className="archived-properties-header">
        <NewCustomButton
          onClick={() => navigate('/properties')}
          variant="outlined"
          sx={{ 
            mb: 2,
            borderRadius: '9999px',
            textTransform: 'none',
            fontWeight: 600,
            height: '40px',
            padding: '8px 16px',
            minWidth: '140px'
          }}
        >
          Back to Properties
        </NewCustomButton>
        <h1>Archived Properties</h1>
      </div>

      {archivedProperties.length === 0 ? (
        <Alert severity="info">No archived properties found</Alert>
      ) : (
        <Grid container spacing={2}>
          {archivedProperties.map((property) => (
            <Grid item xs={12} key={property.id}>
              <div className="archived-property-item" role="article">
                <div className="property-content">
                  <Box>
                    <div className="property-address">
                      {property.address}
                    </div>
                    <div className="archive-date">
                      Archived on: {formatDate(property.archivedDate)}
                    </div>
                  </Box>
                  <Box>
                    <Tooltip title="Unarchive">
                      <IconButton 
                        onClick={() => handleUnarchive(property.id)}
                        className="unarchive-button"
                        aria-label="Unarchive property"
                      >
                        <FaUndo />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </div>
              </div>
            </Grid>
          ))}
        </Grid>
      )}
    </div>
  );
};

export default ArchivedProperties;